import React, { useEffect, useState } from "react";
import { Bell, X, CheckCircle } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import usePushSubscription from "@/lib/usePushSubscription";
import { toast } from "sonner";

/** First-week check-in opt-in. Render this once after the first booking is
 * confirmed, the modal will silently no-op if the browser doesn't support
 * push, the user already opted in, or they've dismissed it before. */
export default function PushOptInModal({ bookingId, autoShow = true, onClose }) {
  const { supported, permission, subscribed, busy, subscribe, scheduleFirstWeekCheckin, markPrompted, wasPrompted } = usePushSubscription();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!autoShow) return;
    if (!supported || subscribed || permission === "denied" || wasPrompted()) return;
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, [autoShow, supported, subscribed, permission, wasPrompted]);

  const close = () => {
    setOpen(false);
    markPrompted();
    onClose?.();
  };

  const enable = async () => {
    const r = await subscribe();
    if (!r.ok) {
      if (r.reason === "permission_denied") toast.error("You can re-enable notifications later from your browser settings.");
      else toast.error("Couldn't enable reminders right now.");
      close();
      return;
    }
    // Schedule the 6-day check-in once subscription is registered.
    await scheduleFirstWeekCheckin(bookingId);
    toast.success("Thanks, we'll check in after your first week.");
    close();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="md-pushoptin-modal" data-testid="push-opt-in-modal">
        <button onClick={close} className="md-pushoptin-close" aria-label="Close" data-testid="push-opt-in-close">
          <X size={16} />
        </button>

        <div className="md-pushoptin-icon" aria-hidden>
          <Bell size={28} weight="duotone" />
        </div>

        <div className="md-label-eyebrow mb-2">A gentle nudge</div>
        <h2 className="md-pushoptin-title">Want a check-in after your first week?</h2>
        <p className="md-pushoptin-sub">
          We&apos;ll send you one tiny notification six days from now, just a quick
          &ldquo;how did the first session go?&rdquo; No spam, ever. Unsubscribe anytime.
        </p>

        <ul className="md-pushoptin-bullets">
          <li><CheckCircle size={14} weight="duotone" /> One reminder. Not a series.</li>
          <li><CheckCircle size={14} weight="duotone" /> Tap to share how you&apos;re doing.</li>
          <li><CheckCircle size={14} weight="duotone" /> Stays on your device, fully private.</li>
        </ul>

        <div className="md-pushoptin-actions">
          <button onClick={close} className="btn-outline text-sm" data-testid="push-opt-in-skip">Maybe later</button>
          <button onClick={enable} disabled={busy} className="btn-primary text-sm" data-testid="push-opt-in-enable">
            <Bell size={14} weight="bold" /> {busy ? "Setting up…" : "Yes, remind me"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
